import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Shield, BarChart3 } from "lucide-react";
import { useCookieConsent } from "@/hooks/useCookieConsent";

interface CookieSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
}

export function CookieSettingsDialog({
  open,
  onOpenChange,
  onSaved,
}: CookieSettingsDialogProps) {
  const { getConsentStatus, acceptCustom, logConsentToAuditTrail } =
    useCookieConsent();

  const currentStatus = getConsentStatus();
  const [analyticsEnabled, setAnalyticsEnabled] = useState(
    currentStatus === "all"
  );

  const handleSave = () => {
    const categories = { necessary: true, analytics: analyticsEnabled };
    acceptCustom(analyticsEnabled);
    logConsentToAuditTrail(analyticsEnabled, categories);
    onOpenChange(false);
    onSaved?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Cookie-Einstellungen</DialogTitle>
          <DialogDescription>
            Hier kannst du auswählen, welche Cookies du zulassen möchtest.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Necessary Cookies */}
          <div className="flex items-start gap-4">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Shield className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <Label className="font-semibold text-foreground">
                  Notwendige Cookies
                </Label>
                <Switch checked disabled className="opacity-70" />
              </div>
              <p className="text-muted-foreground text-sm">
                Diese Cookies sind für die grundlegende Funktionalität der Website
                erforderlich und können nicht deaktiviert werden.
              </p>
            </div>
          </div>

          {/* Analytics Cookies */}
          <div className="flex items-start gap-4">
            <div className="w-9 h-9 rounded-lg bg-accent flex items-center justify-center flex-shrink-0 mt-0.5">
              <BarChart3 className="h-4 w-4 text-accent-foreground" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <Label
                  htmlFor="analytics-switch"
                  className="font-semibold text-foreground cursor-pointer"
                >
                  Analyse-Cookies
                </Label>
                <Switch
                  id="analytics-switch"
                  checked={analyticsEnabled}
                  onCheckedChange={setAnalyticsEnabled}
                />
              </div>
              <p className="text-muted-foreground text-sm">
                Google Analytics hilft uns zu verstehen, wie Besucher unsere Website
                nutzen. Die Daten werden anonymisiert erhoben.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button onClick={handleSave}>Auswahl speichern</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
